import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth, api } from "@/lib/auth";
import { rememberLoginEmail } from "@/lib/loginPrefill";
import { isEmailShaped, suggestEmailFix } from "@/lib/emailHint";
import { ymGoalThen } from "@/lib/analytics";
import { PRO_GRADIENT } from "@/lib/themePalette";

/**
 * Регистрация одной строкой: почта + галочка согласия, без пароля, автовход.
 *
 * Выделено из NatalLimitModal без изменений в поведении: там этот шаг дал
 * регистрации там, где переход на /auth/register терял 71% людей (окно видели
 * 71 человек, до страницы доходили 18). Хорару нужен ровно тот же шаг, и
 * второй такой же формы в проекте быть не должно — иначе две точки правды на
 * проверку адреса, ловушку для ботов и ветку «адрес уже зарегистрирован».
 *
 * Что здесь есть, кроме поля и кнопки:
 *   — проверка вида адреса (isEmailShaped) и подсказка опечатки в домене
 *     (suggestEmailFix): адрес — единственный вход в аккаунт, пароля нет,
 *     и «mail.ri» вместо «mail.ru» означает, что человек не войдёт никогда.
 *     Подсказка НЕ блокирует отправку навсегда: второе нажатие отправляет
 *     как есть — редкий домен должен пройти;
 *   — поле-ловушка для ботов (скрыто от человека и от скринридера);
 *   — защита от двойного нажатия через ref (состояние ставится асинхронно);
 *   — ветка «этот адрес уже зарегистрирован»: главный выход — прислать
 *     ссылку для входа НА ТОТ ЖЕ адрес, который человек только что ввёл
 *     (чужой ящик отсюда не засыпать).
 *
 * Чего здесь НЕТ намеренно:
 *   — своих текстов сверху окна, тоста успеха и экрана успеха. Всё это
 *     решает вызывающий код: у натала свои утверждённые тексты, у хорара
 *     свои. Форма вызывает onRegistered(email) и больше ничего не делает —
 *     поэтому натальное окно после выделения ведёт себя символ в символ так
 *     же, как вело;
 *   — своих целей Метрики. Имя цели и place передаются в submitGoal:
 *     у натала это signup_submit / modal_inline, у хорара — signup_click
 *     с place=horar_limit и place=horar_email. Новых целей не заводим.
 *
 * Состояние формы живёт ЗДЕСЬ, а не у родителя. Оба вызывающих окна
 * возвращают null, когда закрыты, поэтому форма размонтируется вместе с
 * ними и на следующем открытии не показывает чужую ошибку или подсказку от
 * прошлого раза — сбрасывать руками нечего.
 */
// Тексты окна регистрации (14.10.2026) — дословно как в журнале согласий
// (backend/legal_documents.py: SERVICE_FORECAST_TEXT, PROMO_CHECKBOX_TEXT).
export const FORECAST_SERVICE_NOTE = 'Ежедневные прогнозы 14 дней приходят на почту — это часть услуги. Отписаться можно в любом письме';
export const PROMO_CHECKBOX_LABEL = 'Получать акции и предложения';

export default function OneLineSignupForm({
  // Идентификаторы: data-testid собираются как `${idPrefix}-email-input` и
  // т.д. У натала idPrefix = natal-wall, поэтому все его прежние testid
  // остаются на своих местах.
  idPrefix,
  submitTestId,
  submitLabel,
  loadingLabel,
  emailLabel = "Твоя почта",
  // Строка под кнопкой. Про отсутствие пароля говорим прямо, иначе человек
  // ищет второе поле и думает, что форма недогрузилась.
  footNote = "Пароль не нужен: вход по ссылке из письма. Бесплатно, без привязки карты.",
  // Галочка «Получать акции и предложения» — ПУСТАЯ по умолчанию
  // (14.10.2026). Ежедневные прогнозы — сама обещанная услуга «14 дней
  // прогнозов» и идут без галочки: об этом строка над ней. Значение уходит на
  // сервер (promo_consent) и ложится в журнал согласий тем же запросом.
  consentDefault = false,
  // Имя из гостевой формы расчёта, если человек его указал: первое же письмо
  // начнётся с «Привет, Имя!».
  collectFirstName,
  // { name, place } — цель на ОТПРАВКУ формы, до запроса: она отделяет
  // «увидел окно и не отправил» от «отправил и не получилось».
  submitGoal,
  // place, который уходит в register() и дальше в цель signup_completed.
  registerPlace,
  // Аккаунт создан и человек уже вошёл. Всё остальное — тост, экран успеха,
  // засев профиля, что открыть под окном — делает вызывающий код.
  onRegistered,
  className = "px-6 pb-6 space-y-3",
}) {
  const navigate = useNavigate();
  const { register } = useAuth();

  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(consentDefault);
  const [loading, setLoading] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [linkSending, setLinkSending] = useState(false);
  const [linkSentTo, setLinkSentTo] = useState("");
  const [typoFix, setTypoFix] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const submittingRef = useRef(false);

  // Ссылка для входа на занятый адрес. Уходит на ТОТ ЖЕ адрес, что человек
  // только что ввёл.
  // Куда вернуть человека после входа — туда, где он сейчас стоит. Он не
  // «пришёл войти», он вводил данные в калькуляторе и упёрся в то, что
  // адрес уже занят: привести его в профиль значит бросить на полпути.
  // Сервер и страница входа всё равно сверяют адрес с белым списком.
  const currentPath = () => {
    try {
      return window.location.pathname + window.location.search;
    } catch (_e) {
      return "";
    }
  };

  const sendLoginLinkToTakenEmail = async () => {
    const value = email.trim();
    if (!value) return;
    setLinkSending(true);
    try {
      await api("POST", "/auth/login-link", { email: value, return_to: currentPath() });
      setLinkSentTo(value);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : "Не получилось отправить письмо. Попробуй войти с этим адресом.");
    } finally {
      setLinkSending(false);
    }
  };

  const goToLoginWithThisEmail = () => {
    const value = email.trim();
    rememberLoginEmail(value);
    // С возвратом на ту же страницу: человек вводил данные в калькуляторе,
    // и после входа он должен увидеть свой расчёт, а не кабинет.
    const back = currentPath();
    navigate(back ? `/auth/login?redirect=${encodeURIComponent(back)}` : "/auth/login",
             { state: { email: value } });
  };

  const submit = async (value) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    setEmailTaken(false);
    try {
      let guestName = "";
      if (typeof collectFirstName === "function") {
        try { guestName = collectFirstName() || ""; } catch (_e) {}
      }
      await register(value, null, {
        emailMarketingConsent: true,
        promoConsent: consent,
        firstName: guestName,
        honeypot,
        place: registerPlace,
      });
      if (typeof onRegistered === "function") onRegistered(value);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      const text = typeof detail === "string" ? detail : "";
      if (status === 429) {
        toast.error(text || "Слишком много попыток. Попробуй позже.");
      } else if (
        text.toLowerCase().includes("уже зарегистрирован") ||
        status === 409
      ) {
        setEmailTaken(true);
      } else if (Array.isArray(detail)) {
        toast.error("Проверь адрес почты");
      } else if (error?.message?.includes("fetch") || error?.code === "ERR_NETWORK") {
        toast.error("Сервер недоступен. Проверь подключение.");
      } else {
        toast.error(text || "Не получилось зарегистрировать. Попробуй ещё раз.");
      }
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!isEmailShaped(value)) {
      toast.error("Проверь адрес: нужен вид имя@почта.ру");
      return;
    }
    const fix = suggestEmailFix(value);
    if (fix && fix !== typoFix) {
      setTypoFix(fix);
      return;
    }
    // С колбэком: после ответа экран меняется, и без форы Метрика может
    // не успеть.
    ymGoalThen(submitGoal.name, { place: submitGoal.place }, () => { submit(value); });
  };

  return (
    <form onSubmit={handleSubmit} className={className} noValidate>
      <div className="space-y-1.5 text-left">
        <Label htmlFor={`${idPrefix}-email`} className="text-xs font-medium text-slate-600">
          {emailLabel}
        </Label>
        <Input
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailTaken(false);
            setTypoFix("");
            setLinkSentTo("");
          }}
          disabled={loading}
          required
          data-testid={`${idPrefix}-email-input`}
        />
        {/* Ловушка: скрыта от человека и от скринридера, роботы её
            заполняют. tabIndex -1 и aria-hidden — чтобы на неё нельзя
            было попасть с клавиатуры. */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        />
        {typoFix && (
          <p className="text-xs text-amber-700" data-testid={`${idPrefix}-typo-hint`}>
            Возможно, вы имели в виду{" "}
            <button
              type="button"
              className="font-semibold underline underline-offset-2"
              onClick={() => { setEmail(typoFix); setTypoFix(""); }}
              data-testid={`${idPrefix}-typo-fix-btn`}
            >
              {typoFix}
            </button>
            ? Если адрес верный — нажми «{submitLabel}» ещё раз.
          </p>
        )}
      </div>

      <p className="text-left text-xs leading-snug text-slate-600" data-testid={`${idPrefix}-forecast-note`}>
        {FORECAST_SERVICE_NOTE}
      </p>
      <div className="flex items-start gap-2 text-left" data-testid={`${idPrefix}-consent-row`}>
        <Checkbox
          id={`${idPrefix}-consent`}
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          disabled={loading}
          className="mt-0.5"
          data-testid={`${idPrefix}-consent-checkbox`}
        />
        <div className="space-y-1">
          <Label
            htmlFor={`${idPrefix}-consent`}
            className="text-xs text-slate-600 leading-snug cursor-pointer font-normal block"
          >
            {PROMO_CHECKBOX_LABEL}
          </Label>
          <Link
            to="/mailing-consent"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 underline underline-offset-2 hover:text-blue-700"
            data-testid={`${idPrefix}-consent-link`}
          >
            Согласие на рассылку
          </Link>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 text-white font-semibold text-base"
        style={{ background: PRO_GRADIENT, border: "none" }}
        data-testid={submitTestId}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {loadingLabel}
          </>
        ) : (
          submitLabel
        )}
      </Button>

      {footNote && (
        <p className="text-xs text-slate-400 text-center">
          {footNote}
        </p>
      )}

      {emailTaken && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left"
          data-testid={`${idPrefix}-email-taken`}
        >
          <p className="text-sm font-semibold text-amber-900">
            Этот адрес уже зарегистрирован
          </p>
          <p className="mt-1 text-xs text-amber-800 leading-snug">
            Заново регистрироваться не нужно — пришлём ссылку для входа на
            этот адрес.
          </p>
          {linkSentTo ? (
            <p
              className="mt-3 text-sm text-amber-900"
              data-testid={`${idPrefix}-email-taken-link-sent`}
            >
              Ссылка для входа отправлена на {linkSentTo}. Письмо приходит за
              минуту — проверь «Входящие» и «Спам».
            </p>
          ) : (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              className="gap-2"
              onClick={sendLoginLinkToTakenEmail}
              disabled={linkSending}
              data-testid={`${idPrefix}-email-taken-link-btn`}
            >
              {linkSending
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <LogIn className="w-4 h-4" />}
              Прислать ссылку для входа
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-2 bg-white"
              onClick={goToLoginWithThisEmail}
              data-testid={`${idPrefix}-email-taken-login-btn`}
            >
              <LogIn className="w-4 h-4" />
              Войти
            </Button>
            <Link
              to="/forgot-password"
              className="text-sm text-blue-600 underline underline-offset-2 hover:text-blue-700"
              data-testid={`${idPrefix}-email-taken-forgot-link`}
            >
              Забыли пароль?
            </Link>
          </div>
          )}
        </div>
      )}
    </form>
  );
}
